import React, { useMemo, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import {
    Button,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    makeStyles,
    useMediaQuery
} from '@ellucian/react-design-system/core';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { CONSTANTS } from '../Helpers/Configuration';
import classnames from 'classnames';
import { colorTextAlertError, colorTextNeutral200, spacing30 } from '@ellucian/react-design-system/core/styles/tokens';
import get from 'lodash/get';
import { getRecordNotFoundAttributeForTable } from '../Helpers/ApplicationFilteredSearchHelpers';
import { formatPhoneNumber } from '../Helpers/PhoneHelper';
import { createSelector } from '@reduxjs/toolkit';
import { batch, useDispatch, useSelector } from 'react-redux';
import GlobalLocaleContext from '../GlobalLocale/GlobalLocaleContext';
import useApplicationForm from './useApplicationForm';
import { setError, setForceShowErrors } from '@nsr/react-form';
import { selectCurriculumValues } from './redux/slices/applicationSlice';
const useStyles = makeStyles(() => ({
    error: {
        color: colorTextAlertError,
    },
    label: {
        textTransform: 'uppercase !important',
    },
    value: {
        whiteSpace: 'pre-wrap',
    },
    root: {
        display: 'flex',
        flexDirection: 'column',
    },
    tableExpandableRow: {
        padding: ['0px', '!important'],
        margin: ['0px', '!important']
    },
    tableContainer: {
        background: colorTextNeutral200,
        padding: spacing30
    },
    tableCellNoBorder: {
        border: 'none'
    },
    showButton: {
        marginLeft: spacing30
    }
}));

const selectApplicationId = state => state.application?.id;

const QuestionResponse = props => {
    const intl = useIntl();
    const { question, prefix, value, piiHidden, isConditionallyRequired } = props;
    const classes = useStyles();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const noResponse = intl.formatMessage({ id: 'noResponseEnteredTextId' });
    const { globalLocale } = useContext(GlobalLocaleContext);
    const { adminTenant, previewMode, adminApi } = useApplicationForm();
    const tenant = window.PRELOADED_STATE?.globalSettings || adminTenant;
    const applicationId = useSelector(selectApplicationId);
    const dateFormat = (tenant?.settingMessages?.languages[globalLocale]?.dateFormat || 'MM/dd/yyyy').toUpperCase();
    const [showMaskedValue, setShowMaskedValue] = useState(false);
    const questionPath = useMemo(() => `${prefix}.${question.id}`, [prefix, question]);
    const pageId = useMemo(() => {
        const firstDotIndex = prefix.indexOf('.');

        return firstDotIndex >= 0 ? prefix.substring(0, firstDotIndex) : prefix;
    }, [prefix]);
    const repetition = useMemo(() => {
        // If we are in a repeating section,
        // The repetition index is the third part of the prefix.
        let rep = undefined;
        const parts = prefix?.split?.call(prefix, '.');

        if (parts?.length === 3) {
            try {
                rep = parseInt(parts[2]);
            } catch {
                // Assume no repetition.
            }
        }

        return rep;
    }, [prefix]);
    const selectQuestionErrors = useMemo(() => {
        return createSelector(
            state => get(state.form.errors, questionPath, false),
            value => !!value
        );
    }, [questionPath]);
    const hasErrors = useSelector(selectQuestionErrors);
    const selectForceShowErrors = useMemo(
        () => createSelector(
            state => state.form.meta.forceShowErrors[pageId],
            value => value
        ),
        [pageId]
    );
    const forceShowErrors = useSelector(selectForceShowErrors);
    const isMajorDescription = question.id === CONSTANTS.majorDescriptionSelfServiceQuestionId;
    const curriculumValues = useSelector(selectCurriculumValues);

    const rootClasses = useMemo(() => {
        return classnames(classes.root, {
            [classes.error]: hasErrors,
        });
    }, [classes, hasErrors]);

    const labelClasses = useMemo(() => {
        return classnames(classes.label, {
            [classes.error]: hasErrors,
        });
    }, [classes, hasErrors]);

    const isValidRegex = useMemo(() => {
        try {
            // Try/Catch regex to determine if it is valid. Use this knowledge when presenting errors in UI
            // If an admin is viewing the page then alert them to the regex issue
            // If a student is viewing the page then hide any issues from them
            new RegExp(question.regexValidationExpression);
            return true;
        }
        catch {
            return false;
        }
    }, [question.regexValidationExpression]);

    useEffect(() => {
        if ((question.required || isConditionallyRequired) && !value) {
            batch(() => {
                dispatch(setError({
                    name: questionPath,
                    value: intl.formatMessage({ id: 'validateRequired' })
                }));
                if (!forceShowErrors) {
                    dispatch(setForceShowErrors({
                        name: pageId,
                        value: true
                    }));
                }
            });
        }
        if (question.showRegexValidation && question.regexValidationExpression && value) {
            let isValid = true;
            let errorMsgId = 'invalidRegexValue';

            if (piiHidden && question.inputType === 'password') {
                isValid = value !== CONSTANTS.piiMaskedValueRegexError;
            } else if (!isValidRegex) {                    
                // If the regex is invalid (edge case) then only dispatch an error if it is an admin viewing the value
                // If this is a student viewing this then nothing to see here, pass it.
                errorMsgId = 'invalidRegexExpression';
                isValid = !previewMode;
            } else {
                var regularExpressionValidation = new RegExp(question.regexValidationExpression);
                isValid = regularExpressionValidation.test(value);
            }
            if (!isValid) {
                dispatch(setError({
                    name: questionPath,
                    value: intl.formatMessage({ id: errorMsgId })
                }));
                if (!forceShowErrors) {
                    dispatch(setForceShowErrors({
                        name: pageId,
                        value: true
                    }));
                }
            }
        }
    }, [previewMode, isValidRegex, question.showRegexValidation, question.regexValidationExpression, question.inputType, piiHidden, forceShowErrors, questionPath, question.required, isConditionallyRequired, value]);

    const buildATypographyRenderValue = (text) => {
        return (
            <Typography variant="body1">{text}</Typography>
        );
    };

    const buildShowMaskedValue = (value, maskedValue) => {
        return (
            <div>
                <Typography variant="body1" display="inline">{showMaskedValue ? value : maskedValue}</Typography>
                <Button className={classes.showButton} variant="text" onClick={() => {
                    const newShowMaskedValue = !showMaskedValue;
                    setShowMaskedValue(newShowMaskedValue);

                    if (adminApi && !previewMode && applicationId) {
                        if (newShowMaskedValue) {
                            adminApi.applicationForms.unmaskPii(applicationId, label, repetition);
                        } else {
                            adminApi.applicationForms.maskPii(applicationId, label, repetition);
                        }
                    }
                }}>
                    {intl.formatMessage({ id: showMaskedValue ? 'hide' : 'show' })}
                </Button>
            </div>
        );
    };

    const getValueFromRecord = (record, field) => {
        if (!field.path || field.path.length === 0) {
            return record[field.value] || record.label || '';
        }
        const pathValues = field.path.map(p => p.value);
        const keyName = `${pathValues.join('.')}.${field.value}`;
        return record[keyName] || record.label || '';
    };

    const buildFilteredSearchRenderValue = (filteredSearch, value) => {
        if (!value || !value?.id) {
            return buildATypographyRenderValue(noResponse);
        }

        let recordValue = value;
        if (value.id.includes('_noRecordFound')) {
            recordValue = getRecordNotFoundAttributeForTable(filteredSearch.recordNotFoundAttribute, intl.formatMessage);
        }
        const displayFields = filteredSearch?.displayAttributes ?? [];
        if (!displayFields || displayFields.length === 0 || !recordValue) {
            return buildATypographyRenderValue(noResponse);
        }

        const noRecordFoundColSpan = (Object.keys(recordValue)?.length - 1) !== displayFields?.length ? displayFields.length : 1;
        const tableHeadsLabels = displayFields.map((field) => intl.formatMessage({ id: field.displayLabelId }));
        return (
            <div className={classes.tableContainer}>
                <Table
                    layout={{
                        variant: 'card',
                        breakpoint: isMobile ? 'lg' : 'md',
                    }}
                    size={'small'}
                >
                    <TableHead>
                        <TableRow>
                            {tableHeadsLabels?.map((label, labelIndex) => {
                                return (
                                    <TableCell align="left" key={`TableHead_TableCell_${labelIndex}`}>
                                        {label}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow className={classes.tableExpandableRow}>
                            {Object.keys(recordValue)?.slice(1)?.length === 1 && recordValue.label && (// Record not found
                                <TableCell
                                    colSpan={noRecordFoundColSpan}
                                    align={'left'}
                                    columnName={tableHeadsLabels[0]}
                                    className={classes.tableCellNoBorder}
                                >
                                    {recordValue.label || ''}
                                </TableCell>
                            )}
                            {(Object.keys(recordValue)?.slice(1)?.length > 1 || !recordValue.label) &&
                                displayFields?.map((field, fieldIndex) => {
                                    return (
                                        <TableCell
                                            key={`TableBody_TableCell_${field.value}_${fieldIndex}`}
                                            align={'left'}
                                            columnName={tableHeadsLabels[fieldIndex]}
                                            className={classes.tableCellNoBorder}
                                        >
                                            {getValueFromRecord(recordValue, field)}
                                        </TableCell>
                                    );
                                })}
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        );
    };

    const renderValue = useMemo(() => {
        if (value === null || value === undefined || value === '') {
            return buildATypographyRenderValue(noResponse);
        }
        if (question.showRegexValidation && question.regexValidationExpression && value !== CONSTANTS.piimaskedValue) {
            if (question.inputType === 'password' && value === CONSTANTS.piiMaskedValueRegexError) {
                return buildATypographyRenderValue(intl.formatMessage({ id: 'invalidRegexValue' }));
            }
            else {
                if (isValidRegex)
                {
                    var regularExpressionValidation = new RegExp(question.regexValidationExpression);
                    if (!regularExpressionValidation.test(value)) {
                        return buildATypographyRenderValue(intl.formatMessage({ id: 'invalidRegexValue' }));
                    }
                }
                else if (previewMode)
                {
                    //Only render field with invalid expression if an admin is in preview
                    //Otherwise let code continue to render the value as is since it passes the broken regex for students
                    return buildATypographyRenderValue(intl.formatMessage({ id: 'invalidRegexExpression' }));
                }
            }
        }
        let output = value;
        let found, range, from, to;
        let newOutput = [];

        switch (question.type) {
            case CONSTANTS.controlTypes.datepicker:
                output = moment(value).format(dateFormat);
                break;
            case CONSTANTS.controlTypes.daterange:
                range = value.split(',');
                [from, to] = range;
                if (range.length === 1 || (!from && !to)) {
                    output = noResponse;
                    break;
                }
                output = intl.formatMessage({ id: 'dateRangeQuestionResponse' }, {
                    from: from ? moment(from).format(dateFormat) : intl.formatMessage({ id: 'notSpecified' }),
                    to: to ? moment(to).format(dateFormat) : intl.formatMessage({ id: 'notSpecified' }),
                });
                break;
            case CONSTANTS.controlTypes.searchandselect:
            case CONSTANTS.controlTypes.checkbox:
                if (Array.isArray(output) && output.length > 0) {
                    output.forEach(item => {
                        found = question.possibleValues.values.find(it => it.value === item);
                        if (found) {
                            if (question.possibleValues.type === CONSTANTS.questionPossibleValueTypes.dynamic) {
                                found.label && newOutput.push(found.label);
                            }
                            else if (found.labelId) {
                                newOutput.push(intl.formatMessage({ id: found.labelId }));
                            }
                            else {
                                newOutput.push(item);
                            }
                        }
                    });
                    output = newOutput.join(', ');
                }
                else {
                    output = noResponse;
                }
                break;
            case CONSTANTS.controlTypes.radiogroup:
                found = question.possibleValues.values.find(it => it.value === value);
                if (found && found.labelId) {
                    output = intl.formatMessage({ id: found.labelId });
                }
                break;
            case CONSTANTS.controlTypes.singlechoice:
                if (isMajorDescription) {
                    found = curriculumValues?.majorDescriptions?.find(it => it.value === value);
                } else {
                    found = question.possibleValues.values.find(it => it.value === value);
                }

                if (found) {
                    if (isMajorDescription ||
                        question.possibleValues.type === CONSTANTS.questionPossibleValueTypes.dynamic
                    ) {
                        output = found.label || '';
                    }
                    else if (found.labelId) {
                        output = intl.formatMessage({ id: found.labelId });
                    }
                }
                break;
            case CONSTANTS.controlTypes.text:
            case CONSTANTS.controlTypes.textarea:
                if (output && question.inputType === 'password') {
                    output = [...output].map(() => '*').join('');
                    if (!piiHidden) {
                        return buildShowMaskedValue(value, output);
                    }
                }
                break;
            case CONSTANTS.controlTypes.filteredsearch:
                return buildFilteredSearchRenderValue(question.filteredSearch, question.possibleValues?.values?.find(value => value.id === output));
            case CONSTANTS.controlTypes.phone:
                output = formatPhoneNumber(output);
                break;
            default:
                break;
        }
        return buildATypographyRenderValue(output);
    }, [question, intl, value, showMaskedValue, previewMode, isValidRegex, curriculumValues, isMajorDescription]);

    const label = useMemo(() => {
        if (question.labelId) {
            return intl.formatMessage({ id: question.labelId });
        }
        return '';
    }, [intl, question]);

    return (
        <div className={rootClasses}>
            {hasErrors && <Typography className={labelClasses} variant="body3">{intl.formatMessage({ id: 'validateRequiredLabel'})}</Typography>}
            <Typography className={labelClasses} variant="body3">{label}</Typography>
            <div className={classes.value}>{renderValue}</div>
        </div>
    );
};
QuestionResponse.propTypes = {
    question: PropTypes.object.isRequired,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
        PropTypes.object,
    ]),
    prefix: PropTypes.string.isRequired,
    piiHidden: PropTypes.bool,
    isConditionallyRequired: PropTypes.bool
};
export default QuestionResponse;
